import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useCookieConsent } from "./CookieConsentProvider";
import { useTranslation } from "../hooks/useTranslation";

export default function CookieSettingsModal() {
  const { t } = useTranslation();
  const {
    consent,
    isCookieSettingsOpen,
    closeCookieSettings,
    saveConsent,
    acceptAll,
    rejectNonEssential,
  } = useCookieConsent();

  const initialDraft = useMemo(
    () => ({
      necessary: true,
      preferences: Boolean(consent?.preferences),
      analytics: false,
      marketing: false,
    }),
    [consent]
  );

  const [draft, setDraft] = useState(initialDraft);

  useEffect(() => {
    if (isCookieSettingsOpen) setDraft(initialDraft);
  }, [initialDraft, isCookieSettingsOpen]);

  const onSave = () => {
    saveConsent(draft);
  };

  return (
    <Dialog
      open={isCookieSettingsOpen}
      onClose={closeCookieSettings}
      aria-labelledby="cookie-settings-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="cookie-settings-title">{t("cookieSettings")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("cookieSettingsBody")}
          </Typography>

          <Box>
            <FormControlLabel
              control={<Switch checked disabled />}
              label={t("cookieCategoryNecessaryAlwaysOn")}
            />
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.preferences}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      preferences: e.target.checked,
                    }))
                  }
                />
              }
              label={t("cookieCategoryPreferences")}
            />
            <Typography variant="caption" color="text.secondary">
              {t("cookieCategoryPreferencesDesc")}
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.analytics}
                  disabled
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      analytics: e.target.checked,
                    }))
                  }
                />
              }
              label={t("cookieCategoryAnalytics")}
            />
            <Typography variant="caption" color="text.secondary">
              {t("cookieCategoryAnalyticsNotUsedDesc")}
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.marketing}
                  disabled
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      marketing: e.target.checked,
                    }))
                  }
                />
              }
              label={t("cookieCategoryMarketing")}
            />
            <Typography variant="caption" color="text.secondary">
              {t("cookieCategoryMarketingNotUsedDesc")}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" onClick={acceptAll}>
              {t("acceptAll")}
            </Button>
            <Button variant="outlined" onClick={rejectNonEssential}>
              {t("rejectNonEssential")}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeCookieSettings} variant="text">
          {t("close")}
        </Button>
        <Button onClick={onSave} variant="contained" autoFocus>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
